/**
 * ManifestTreeFlow — a STANDALONE, left-to-right React Flow view for GENERATIVE
 * (manifest) org-trees. Deliberately SEPARATE from GraphVisualization (which is tuned
 * for the ASL top-down DAG): a manifest is a pure tree that fans WIDE, so it needs its
 * own layout with generous spacing and horizontal ranks.
 *
 * Intentionally PLAIN React Flow — default node rendering, no custom node types, no
 * portals, no highlight/active plumbing. The one job is: take a ``{nodes, edges}`` graph
 * (as produced by ``manifestToReactFlow``) and lay it out cleanly LEFT → RIGHT so a
 * multi-layer org spreads across the canvas instead of piling into a corner.
 *
 * It does NOT touch or reuse the ASL renderer, so it cannot break it.
 */
import React, { useMemo, memo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';

/** Does this manifest node correspond to the workbench-selected agent (its logicalId)?
 *  Node ids are PATH form (``root/Lead/Specialist``) and ``data.label`` is the logicalId,
 *  so match on the label first, then the id's last segment — mirrors the ASL renderer's
 *  ``nodeMatchesWorkbenchHighlight`` so selection highlights consistently across both. */
function matchesSelected(node, highlightLogicalId) {
  if (!highlightLogicalId) return false;
  const h = String(highlightLogicalId);
  const label = String(node?.data?.label ?? '');
  if (label && label === h) return true;
  const id = String(node?.id ?? '');
  return id === h || id.endsWith(`/${h}`) || id.endsWith(`__${h}`);
}

// Generous, tree-friendly sizing so nodes are readable and never overlap.
const NODE_W = 200;
const NODE_H = 52;
// Big separations = the "spread it out" the org-tree needs. ranksep is the horizontal
// gap between LAYERS (root → leads → specialists); nodesep is the gap between SIBLINGS.
const RANKSEP = 140;   // between depth layers (horizontal, since LR)
const NODESEP = 40;    // between siblings in the same layer (vertical, since LR)

// Leaf-detour routing (a leaf agent with no team that terminates BEFORE the deepest
// stage): its edge to Stop must NOT cut through the later stages' agents. It rises/falls
// into a clear channel just outside the whole node band, runs horizontally across every
// intervening stage there, then drops into the gap just left of Stop and merges into
// Stop with the later stages' exit edges. Dynamic — keys off the laid-out band, so it
// works for any number of layers.
const DETOUR_CH_MARGIN = 28;  // channel clearance beyond the node band (px)
const DETOUR_CH_STEP = 22;    // stagger between parallel detours on the same side (px)
const DETOUR_MERGE_GAP = 24;  // how far left of Stop the detours converge + drop in (px)
const DETOUR_STUB = 20;       // horizontal stub out of the leaf before turning to the channel
const DETOUR_RADIUS = 10;     // corner rounding

/** Kind → a distinct card style (worker vs builder vs start/stop terminals). */
function nodeStyleFor(aslType) {
  if (aslType === 'start') {
    return { background: '#065f46', color: '#fff', border: '1px solid #047857' };
  }
  if (aslType === 'end') {
    return { background: '#7f1d1d', color: '#fff', border: '1px solid #991b1b' };
  }
  if (aslType === 'builder') {
    return { background: '#1e3a8a', color: '#fff', border: '1px solid #2563eb' };
  }
  // worker (default)
  return { background: '#ffffff', color: '#111827', border: '1px solid #6366f1' };
}

// LIVE run-status → node tint. Overlays the base kind-style so the docked room tree reflects the
// AppSync stream in real time: running (blue, pulsing via Tailwind ``animate-pulse``), awaiting
// (amber, pulsing), done (emerald), failed (rose), skipped/not-selected (faded dashed gray), queued
// (default kind-style, untinted). Returns { style, className } to merge onto the node.
function liveStatusStyleFor(status, notSelected) {
  if (notSelected) {
    return { style: { background: '#f8fafc', color: '#94a3b8', border: '1px dashed #cbd5e1', opacity: 0.55 }, className: '' };
  }
  switch (status) {
    case 'running':
      return { style: { background: '#eff6ff', color: '#1e3a8a', border: '2px solid #3b82f6' }, className: 'animate-pulse' };
    case 'awaiting':
      return { style: { background: '#fffbeb', color: '#92400e', border: '2px solid #f59e0b' }, className: 'animate-pulse' };
    case 'done':
      return { style: { background: '#ecfdf5', color: '#065f46', border: '2px solid #10b981' }, className: '' };
    case 'failed':
      return { style: { background: '#fef2f2', color: '#991b1b', border: '2px solid #ef4444' }, className: '' };
    default:
      return null;   // queued / unknown → keep the base kind-style
  }
}

/**
 * ManifestNode — custom tree node. Renders the agent name + the architect's advisory
 * subtitles (capability / model recommendation) inline, and — restoring the behavior the
 * old ASL diagram had — shows the agent's ROLE (``data.comment``, from the manifest's
 * ``description``/``job``) as a rich card on HOVER. Uses the same portal pattern as the ASL
 * ``AgentNode`` so the tooltip layers above the canvas and never clips inside overflow.
 * Terminals (start/stop) have no role → plain label, no hover.
 */
const ManifestNode = memo(({ data }) => {
  const aslType = data?.aslType;
  const isAgent = aslType === 'builder' || aslType === 'worker';
  const name = data?.label ?? '';
  const role = isAgent ? String(data?.comment || '').trim() : '';
  const hasRole = role.length > 0;
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0 });

  const onEnter = (e) => {
    if (!hasRole) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ show: true, x: r.left + r.width / 2, y: r.top });
  };
  const onLeave = () => setTooltip((t) => ({ ...t, show: false }));

  return (
    <>
      <div
        className="flex h-full w-full flex-col items-center justify-center overflow-hidden text-center"
        style={{ lineHeight: 1.2, cursor: hasRole ? 'help' : 'default' }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <Handle type="target" position={Position.Left} className="!bg-gray-400" />
        <span className="w-full truncate px-1 font-medium">{name}</span>
        <Handle type="source" position={Position.Right} className="!bg-gray-400" />
      </div>
      {hasRole && tooltip.show && createPortal(
        <div
          className="fixed z-[99999] pointer-events-none rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-normal text-gray-800 shadow-xl"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, calc(-100% - 8px))',
            whiteSpace: 'pre-wrap',
            minWidth: 320,
            maxWidth: 480,
          }}
        >
          {role}
        </div>,
        document.body,
      )}
    </>
  );
});
ManifestNode.displayName = 'ManifestNode';

const nodeTypes = { manifestNode: ManifestNode };

/** logicalId for a node (label, else last path segment) — the key the status map is keyed by. */
function nodeAgentId(node) {
  const label = String(node?.data?.label ?? '').trim();
  if (label) return label;
  const id = String(node?.id ?? '');
  const seg = id.split('/').pop();
  return seg || id;
}

// Static (kind-independent) node box style — dims + layout. Live tint / kind color / highlight ring
// merge ON TOP of this in ``decorateNode``.
const STATIC_NODE_STYLE = {
  width: NODE_W,
  height: NODE_H,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 6,
};
const HIGHLIGHT_RING = {
  border: '2px solid #f59e0b',
  boxShadow: '0 0 0 3px #fef08a, 0 4px 14px rgba(234, 88, 12, 0.35)',
};

// CAPPED-OUT (manual stage cap): an agent that exists in the org but is DISABLED this release
// because the user limited run depth (stageDepthCap). Dimmed + dashed so it reads as "part of the
// team, not engaged now" — visually distinct enough from a live "skipped" tint (this is a
// deliberate config state, not a per-run routing decision). Applied UNDER highlight so a user can
// still click + inspect it.
const CAPPED_OUT_STYLE = {
  background: '#f8fafc',
  // Darker text than the box tint so the disabled agent's NAME stays readable — the grey
  // background + dashed border already signal "off"; the label shouldn't fade to invisible.
  color: '#64748b',
  border: '1px dashed #cbd5e1',
  opacity: 0.85,
};

// ROOM-DISABLED (per-room agent disable, ROOM-SCOPES Phase 1): a room OWNER switched this agent
// off for THIS room only. Red-tinted + dashed so it reads as a deliberate GOVERNANCE state — a
// harder "off" than the grey capped-out dim (which is a release-wide depth cap, not a per-room
// pick). Applied AFTER live status + cappedOut (the strongest signal short of the selection ring)
// so a stale run tint can never make a disabled agent look active. Descendants of a
// ``'branch'``-disabled agent get the same style (the whole subtree is parked).
const DISABLED_STYLE = {
  background: '#fef2f2',
  color: '#991b1b',
  border: '1px dashed #fca5a5',
  opacity: 0.6,
};

// FROZEN-DISABLED (the room's IMMUTABLE carve — ``source:'frozen'``): the agent was carved out
// when the room was created/released; the room owner CANNOT re-enable it here (only a re-release
// changes it). Colored distinctly from the toggleable room disable above — a solid SLATE lock
// (not red-dashed) so the user reads "structurally not part of this room" vs "I turned this off".
const FROZEN_STYLE = {
  background: '#f1f5f9',
  color: '#475569',
  border: '1px solid #94a3b8',
  opacity: 0.6,
};

// REUSED (RESULT-REUSE.md): a "Reuse last result" mark — the node will NOT re-run; its last output is
// carried into the next run's synthesis. Distinct SKY ring (not a disable) so it reads as an
// intentional, honest "reused" marker, never the same as "done"/disabled.
const REUSED_STYLE = {
  border: '2px solid #38bdf8',
  boxShadow: '0 0 0 2px rgba(56,189,248,0.25)',
};

/**
 * Overlay live run-status + workbench selection onto ONE positioned node — recomputing its
 * ``style``/``className``/``data.highlighted`` from (kind, live status, highlight). Pure + cheap:
 * this runs on every status tick WITHOUT re-laying-out the graph, so ReactFlow reconciles each
 * node in place (positions — incl. user drags — are preserved). ``statusByAgentId`` absent
 * (workbench) → only the base kind-style + highlight apply.
 */
function decorateNode(node, highlightLogicalId, statusByAgentId, dimmedAgentIds, disabledNodeIds, frozenNodeIds, reusedNodeIds) {
  const aslType = node.data?.aslType;
  const isAgentNode = aslType === 'builder' || aslType === 'worker';
  const highlighted = matchesSelected(node, highlightLogicalId);
  // Capped out by the manual stage cap (workbench: live draft cap; room: frozen release cap).
  const cappedOut = !!(dimmedAgentIds && isAgentNode && dimmedAgentIds.has(nodeAgentId(node)));
  // Room-disabled (per-room agent disable): keyed by node PATH id — the coverage set already
  // expanded ``'branch'`` disables to every descendant path (see ``disabledNodeIdSet``), so a
  // subtree dims whole while each node keeps its own label.
  const roomDisabled = !!(disabledNodeIds && isAgentNode && disabledNodeIds.has(String(node.id)));
  // Of the disabled nodes, which are the IMMUTABLE 'frozen' carve (slate lock) vs the owner's
  // toggleable 'room' disable (red-dashed). Frozen wins the color when both cover a node.
  const frozenDisabled = roomDisabled && !!(frozenNodeIds && frozenNodeIds.has(String(node.id)));
  // A capped agent never runs, so ignore any live status tint on it — CAPPED_OUT_STYLE owns its
  // look. This is the room-side fix: without it, once a run settled the roster marked the capped
  // node ``notSelected`` and ``liveStatusStyleFor`` returned its faded (opacity 0.55) style, which
  // — spread AFTER CAPPED_OUT_STYLE — re-faded the node and made the label near-invisible again.
  // Same reasoning for a room-disabled agent: DISABLED_STYLE owns its look.
  const liveEntry = (statusByAgentId && isAgentNode && !cappedOut && !roomDisabled)
    ? statusByAgentId[nodeAgentId(node)] : null;
  const live = liveEntry ? liveStatusStyleFor(liveEntry.status, liveEntry.notSelected) : null;
  return {
    ...node,
    // The custom ``manifestNode`` reads ``data.highlighted``; keep the rest of the data slot.
    data: { ...(node.data || {}), highlighted },
    className: live?.className || undefined,
    style: {
      ...STATIC_NODE_STYLE,
      ...nodeStyleFor(aslType),
      // LIVE status overlay wins over the base kind-style (running/done/skipped/…) for
      // NON-capped nodes.
      ...(live?.style || {}),
      // Capped-out dim is the strongest signal (a disabled stage reads disabled regardless of any
      // stale status) — applied AFTER live so it can't be re-faded by a settled ``notSelected`` tint.
      ...(cappedOut ? CAPPED_OUT_STYLE : {}),
      // Room-disable is the strongest GOVERNANCE signal — it beats live status AND cappedOut
      // (an agent both capped and room-disabled reads room-disabled: the sharper, deliberate off).
      // Two tiers, colored apart: frozen carve = slate lock; owner's own = red-dashed.
      ...(roomDisabled ? (frozenDisabled ? FROZEN_STYLE : DISABLED_STYLE) : {}),
      // Result-reuse ring (RESULT-REUSE.md): a node pinned to reuse its last output. Only when NOT
      // disabled (a disabled node's governance look wins); reads as "kept, not re-run", never "done".
      ...((!roomDisabled && reusedNodeIds && isAgentNode && reusedNodeIds.has(String(node.id))) ? REUSED_STYLE : {}),
      // Selection ring layers on top of everything so an inspected node is always framed.
      ...(highlighted ? HIGHLIGHT_RING : {}),
    },
  };
}

/**
 * Expand the per-room disable map ({agentId → 'branch'|'self'}) into the set of node PATH ids
 * that must render DISABLED. Node ids are paths (``root/Lead/Specialist``), so ``'branch'``
 * coverage is a prefix scan: find each branch-disabled agent's node(s) by ``nodeAgentId``, then
 * include every node whose id starts with that node's ``id + '/'`` (its descendants). ``'self'``
 * covers only the agent's own node(s). Pure; memoized by the component on (graph, map).
 */
function disabledNodeIdSet(graphNodes, disabledByAgentId) {
  const out = new Set();
  if (!disabledByAgentId || typeof disabledByAgentId !== 'object') return out;
  const nodes = Array.isArray(graphNodes) ? graphNodes : [];
  const branchPrefixes = [];
  for (const n of nodes) {
    const mode = disabledByAgentId[nodeAgentId(n)];
    if (mode !== 'branch' && mode !== 'self') continue;
    out.add(String(n.id));
    if (mode === 'branch') branchPrefixes.push(`${String(n.id)}/`);
  }
  if (branchPrefixes.length) {
    for (const n of nodes) {
      const id = String(n.id);
      if (branchPrefixes.some((p) => id.startsWith(p))) out.add(id);
    }
  }
  return out;
}

/**
 * STAGE ALIGNMENT (the shared-Stop misalignment fix) — PURE, exported for test. A node's COLUMN
 * must be its true tree DEPTH from Start, NOT dagre's longest-path rank. The Stop terminal collects
 * an edge from EVERY leaf; with plain edges dagre pulls a SOLO lead (root→lead→Stop) rightward to
 * sit next to Stop, so it lands in the team leads' children column ("stage 2") instead of staying
 * at stage 1 beside its sibling leads. Fix: BFS depth over the TREE edges (excluding leaf→Stop),
 * pin every tree edge to minlen 1 (adjacent ranks), and give each leaf→Stop edge a minlen that
 * lands Stop ONE rank past the DEEPEST agent — so a shallow leaf's edge can't drag it toward Stop.
 *
 * Terminals are identified by ``data.aslType`` (``'start'``/``'end'``) so it's robust to node-id
 * changes. Returns ``{ depth, minlenOf(edge) }`` — ``depth`` maps id→tree depth (Start=0, root
 * cohort=1, …); ``minlenOf`` gives the dagre minlen for an edge (1 for tree edges; the leaf→Stop
 * span for Stop edges).
 * @param {Array} nodes  RF nodes ({id, data:{aslType}})
 * @param {Array} edges  RF edges ({source, target})
 */
export function computeStageRanks(nodes, edges) {
  const inNodes = Array.isArray(nodes) ? nodes : [];
  const inEdges = Array.isArray(edges) ? edges : [];
  const startIds = new Set(inNodes.filter((n) => n.data?.aslType === 'start').map((n) => n.id));
  const stopIds = new Set(inNodes.filter((n) => n.data?.aslType === 'end').map((n) => n.id));
  const stopEdges = [];
  const treeEdges = [];
  for (const e of inEdges) {
    if (!e || !e.source || !e.target) continue;
    (stopIds.has(e.target) ? stopEdges : treeEdges).push(e);
  }
  // BFS depth over the tree edges only (Start = 0, root cohort = 1, their teams = 2, …).
  const adj = {};
  for (const e of treeEdges) (adj[e.source] = adj[e.source] || []).push(e.target);
  const depth = {};
  const queue = [];
  for (const sid of startIds) { depth[sid] = 0; queue.push(sid); }
  while (queue.length) {
    const u = queue.shift();
    for (const v of (adj[u] || [])) {
      if (depth[v] == null) { depth[v] = depth[u] + 1; queue.push(v); }
    }
  }
  const maxAgentDepth = Object.entries(depth)
    .reduce((mx, [id, d]) => (startIds.has(id) ? mx : Math.max(mx, d)), 0);
  const stopRank = maxAgentDepth + 1;   // Stop sits one column past the deepest agent
  const minlenOf = (e) => {
    if (stopIds.has(e.target)) {
      // minlen = ranks from this leaf to Stop, so Stop lands at ``stopRank`` no matter the leaf's
      // depth — a shallow (solo) leaf keeps its own depth instead of being pulled next to Stop.
      const from = depth[e.source];
      return Number.isFinite(from) ? Math.max(1, stopRank - from) : 1;
    }
    return 1;   // tree edge → adjacent ranks
  };
  return { depth, minlenOf, stopRank };
}

/**
 * Rounded orthogonal SVG path through waypoints. Each interior vertex gets a quadratic
 * corner (radius clamped to half the shorter adjacent segment, so zero/tiny segments
 * degrade to a straight join). PURE.
 */
export function roundedPath(pts, r = DETOUR_RADIUS) {
  const p = Array.isArray(pts) ? pts.filter((q) => Array.isArray(q) && q.length === 2) : [];
  if (p.length < 2) return '';
  let d = `M ${p[0][0]},${p[0][1]}`;
  for (let i = 1; i < p.length - 1; i += 1) {
    const [x0, y0] = p[i - 1];
    const [x1, y1] = p[i];
    const [x2, y2] = p[i + 1];
    const len1 = Math.hypot(x1 - x0, y1 - y0) || 1;
    const len2 = Math.hypot(x2 - x1, y2 - y1) || 1;
    const rr = Math.min(r, len1 / 2, len2 / 2);
    const ax = x1 - ((x1 - x0) / len1) * rr;
    const ay = y1 - ((y1 - y0) / len1) * rr;
    const bx = x1 + ((x2 - x1) / len2) * rr;
    const by = y1 + ((y2 - y1) / len2) * rr;
    d += ` L ${ax},${ay} Q ${x1},${y1} ${bx},${by}`;
  }
  const last = p[p.length - 1];
  d += ` L ${last[0]},${last[1]}`;
  return d;
}

/**
 * The waypoint path for one leaf→Stop detour: out of the leaf (right handle) → into the
 * channel (up/down) → across every intervening stage in the channel → down/up in the gap
 * just left of Stop → into Stop's left handle (where the later stages' edges also merge).
 * PURE.
 */
export function buildDetourPath({ sourceX, sourceY, targetX, targetY, channelY }) {
  const mergeX = targetX - DETOUR_MERGE_GAP;
  const stubX = sourceX + DETOUR_STUB;
  const pts = [
    [sourceX, sourceY],
    [stubX, sourceY],
    [stubX, channelY],
    [mergeX, channelY],
    [mergeX, targetY],
    [targetX, targetY],
  ];
  return roundedPath(pts);
}

/**
 * Decide which leaf→Stop edges must detour and at what channel Y. A stop edge detours
 * when its source terminates BEFORE the deepest agent stage (``depth < stopRank - 1`` ⇒
 * ≥1 agent column sits between it and Stop). Each routes through a channel just outside
 * the GLOBAL agent band (guarantees it clears every node), on whichever side is closer to
 * the leaf, staggered so parallel detours don't overlap. Returns ``Map(edgeId → channelY)``.
 * PURE — operates on already-positioned nodes.
 * @param {Array} positionedNodes  [{id, position:{x,y}, data:{aslType}}]
 * @param {Array} edges            [{id, source, target}]
 * @param {{depth:Object, stopRank:number}} ranks  from computeStageRanks
 */
export function computeLeafDetours(positionedNodes, edges, ranks) {
  const out = new Map();
  const nodes = Array.isArray(positionedNodes) ? positionedNodes : [];
  const inEdges = Array.isArray(edges) ? edges : [];
  const depth = ranks?.depth || {};
  const stopRank = Number.isFinite(ranks?.stopRank) ? ranks.stopRank : null;
  if (stopRank == null) return out;

  const box = new Map();
  for (const n of nodes) {
    const x = n?.position?.x;
    const y = n?.position?.y;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    box.set(n.id, {
      top: y, bottom: y + NODE_H, cy: y + NODE_H / 2, aslType: n?.data?.aslType,
    });
  }
  const isTerminal = (t) => t === 'start' || t === 'end';
  // Global agent band (exclude Start/Stop) — the region a detour must clear.
  let bandTop = Infinity;
  let bandBottom = -Infinity;
  for (const b of box.values()) {
    if (isTerminal(b.aslType)) continue;
    bandTop = Math.min(bandTop, b.top);
    bandBottom = Math.max(bandBottom, b.bottom);
  }
  if (!Number.isFinite(bandTop) || !Number.isFinite(bandBottom)) return out;

  const stopIds = new Set(nodes.filter((n) => n?.data?.aslType === 'end').map((n) => n.id));
  const aboveBase = bandTop - DETOUR_CH_MARGIN;
  const belowBase = bandBottom + DETOUR_CH_MARGIN;

  const candidates = [];
  for (const e of inEdges) {
    if (!e || !stopIds.has(e.target)) continue;
    const from = depth[e.source];
    if (!Number.isFinite(from) || from >= stopRank - 1) continue; // adjacent to Stop → normal edge
    const src = box.get(e.source);
    if (!src) continue;
    const side = Math.abs(src.cy - aboveBase) <= Math.abs(src.cy - belowBase) ? 'above' : 'below';
    candidates.push({ id: e.id, side, cy: src.cy });
  }
  // Stagger each side outward, ordered by the leaf's y, so parallel runs stay separate.
  for (const side of ['above', 'below']) {
    const group = candidates.filter((c) => c.side === side).sort((a, b) => a.cy - b.cy);
    group.forEach((c, idx) => {
      out.set(c.id, side === 'above'
        ? aboveBase - idx * DETOUR_CH_STEP
        : belowBase + idx * DETOUR_CH_STEP);
    });
  }
  return out;
}

/** Custom edge: a leaf→Stop line rerouted around the later stages (see computeLeafDetours). */
function LeafDetourEdge({ id, sourceX, sourceY, targetX, targetY, markerEnd, style, data }) {
  const channelY = data?.channelY;
  const d = Number.isFinite(channelY)
    ? buildDetourPath({ sourceX, sourceY, targetX, targetY, channelY })
    : `M ${sourceX},${sourceY} L ${targetX},${targetY}`; // guard: never crash
  return (
    <path id={id} className="react-flow__edge-path" d={d} markerEnd={markerEnd} style={style} fill="none" />
  );
}

const edgeTypes = { leafDetour: LeafDetourEdge };

/**
 * Lay the graph out LEFT→RIGHT with dagre → positioned nodes/edges. PURE TOPOLOGY ONLY (no live
 * status, no highlight) so it's memoized on ``data`` alone and dagre runs ONCE per topology — not
 * on every status tick. Live tint + selection are applied afterwards by ``decorateNode`` in place.
 */
function layoutTopology(graph) {
  const inNodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const inEdges = Array.isArray(graph?.edges) ? graph.edges : [];

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    ranksep: RANKSEP,
    nodesep: NODESEP,
    marginx: 24,
    marginy: 24,
  });
  for (const n of inNodes) {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  }

  // Rank by TREE DEPTH (not longest-path) so solo leads stay at stage 1 — see computeStageRanks.
  const ranks = computeStageRanks(inNodes, inEdges);
  const { minlenOf } = ranks;
  for (const e of inEdges) {
    if (e.source && e.target) g.setEdge(e.source, e.target, { minlen: minlenOf(e) });
  }
  dagre.layout(g);

  const nodes = inNodes.map((n) => {
    const d = g.node(n.id);
    return {
      id: n.id,
      type: 'manifestNode',
      data: { ...(n.data || {}) },
      position: d ? { x: d.x - NODE_W / 2, y: d.y - NODE_H / 2 } : { x: 0, y: 0 },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: { ...STATIC_NODE_STYLE, ...nodeStyleFor(n.data?.aslType) },
    };
  });

  // Which leaf→Stop edges must route AROUND the later stages (and at what channel Y).
  const detours = computeLeafDetours(nodes, inEdges, ranks);

  const edges = inEdges.map((e, i) => {
    const id = e.id || `e-${e.source}-${e.target}-${i}`;
    const channelY = detours.get(id);
    const base = {
      id,
      source: e.source,
      target: e.target,
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { stroke: '#94a3b8', strokeWidth: 1.5 },
    };
    return Number.isFinite(channelY)
      ? { ...base, type: 'leafDetour', data: { channelY } }
      : { ...base, type: 'smoothstep' };
  });

  return { nodes, edges };
}

/**
 * @param {{ data: {nodes:Array, edges:Array}, highlightLogicalId?: string }} props —
 *   the manifest graph (``manifestToReactFlow`` output). ``highlightLogicalId`` amber-rings
 *   the workbench-selected agent's node (Agents tab) AND gently frames it, so the user knows
 *   which node they're editing on a complex tree. ``fitView`` frames the whole tree on load.
 */
export default function ManifestTreeFlow({
  data, highlightLogicalId = null, statusByAgentId = null, onAgentClick = null,
  // Canvas background — defaults to the workbench's gradient; the room's docked tree passes a solid
  // white so the whole tree block reads as one clean white surface.
  canvasClassName = 'bg-gradient-to-br from-gray-50 to-blue-50',
  // Manual stage cap: agentIds whose stage-depth exceeds the cap → rendered DIMMED ("disabled this
  // release; part of the team, not engaged"). Set (or null). Workbench passes the live-draft cap;
  // the room passes the frozen-release cap. See ``cappedOutAgentIds``.
  dimmedAgentIds = null,
  // ROOM-SCOPES Phase 1 — per-room agent disables ({agentId → 'branch'|'self'} or null). A
  // ``'branch'`` mode dims the agent's WHOLE subtree; ``'self'`` only the agent itself. Room
  // surface only — the workbench never passes it.
  disabledByAgentId = null,
  // TWO-TIER source per disabled agent ({agentId → 'frozen'|'room'} or null). 'frozen' = the
  // room's IMMUTABLE carve (slate lock, no Enable action); 'room' = owner's toggleable disable
  // (red-dashed). Absent → all disables treated as 'room'.
  disabledSourceByAgentId = null,
  // Right-click disable menu (room surface only): when provided, a node's context menu offers
  // "Disable node" / "Bypass agent" / "Enable agent". action ∈
  // 'disable-branch' | 'disable-self' | 'enable'. Absent (workbench Agents tab) → no menu.
  onAgentDisableAction = null,
  // RESULT REUSE (RESULT-REUSE.md, room surface, project/phased only): per-agent reuse marks
  // ({agentId → {capturedMilestone, capturedRunId}} or null) + the action handler
  // (action ∈ 'reuse' | 'stop-reuse'). When ``onAgentReuseAction`` is provided the node menu offers
  // "Reuse last result"; a reused node is ringed + shows "Reused from run N". Absent → no reuse UI
  // (non-project networks / the workbench).
  reuseByAgentId = null,
  onAgentReuseAction = null,
}) {
  // TOPOLOGY LAYOUT — dagre runs ONCE per topology (memoized on ``data`` only), NOT on every
  // status tick. This is the fix for the live-run graph inconsistency: previously dagre re-ran +
  // a fresh ``nodes`` array was handed to an uncontrolled ReactFlow on every status change, which
  // ReactFlow doesn't reliably reconcile (hence tints landing only after a hard refresh).
  const { nodes: baseNodes, edges } = useMemo(() => layoutTopology(data), [data]);

  // Per-room disable coverage: expand {agentId → mode} to the node-PATH ids that must dim —
  // ``'branch'`` includes the whole subtree (prefix scan over the raw graph nodes). Recomputes
  // only when the topology or the disable map changes, not on status ticks.
  const disabledNodeIds = useMemo(
    () => disabledNodeIdSet(data?.nodes, disabledByAgentId),
    [data, disabledByAgentId],
  );

  // FROZEN coverage: the subset of disabled nodes whose source is 'frozen' (immutable carve),
  // expanded to subtrees the same way (a frozen 'branch' dims its whole subtree in slate). Built
  // by filtering the disable map to frozen agents, then reusing the same prefix-scan expander.
  const frozenNodeIds = useMemo(() => {
    if (!disabledSourceByAgentId || !disabledByAgentId) return null;
    const frozenMap = {};
    for (const [aid, mode] of Object.entries(disabledByAgentId)) {
      if (disabledSourceByAgentId[aid] === 'frozen') frozenMap[aid] = mode;
    }
    return disabledNodeIdSet(data?.nodes, frozenMap);
  }, [data, disabledByAgentId, disabledSourceByAgentId]);

  // Node ids carrying a reuse mark (RESULT-REUSE.md) — ringed so a reused node is visibly
  // intentional. Declared HERE (before the decorate effects that consume it), alongside
  // disabledNodeIds/frozenNodeIds — a later declaration would be a TDZ crash in those effects.
  const reusedNodeIds = useMemo(() => {
    const out = new Set();
    if (!reuseByAgentId || typeof reuseByAgentId !== 'object') return out;
    for (const n of (Array.isArray(data?.nodes) ? data.nodes : [])) {
      if (reuseByAgentId[nodeAgentId(n)]) out.add(String(n.id));
    }
    return out;
  }, [data, reuseByAgentId]);

  // CONTROLLED nodes: ReactFlow owns the node store (positions, drags). We seed it from the
  // positioned topology and mutate style/className/highlight IN PLACE when status/selection change.
  const [nodes, setNodes, onNodesChange] = useNodesState(baseNodes);

  // Re-seed the store when the TOPOLOGY changes (new network / release), applying the current live
  // overlay so the first paint is already tinted.
  useEffect(() => {
    setNodes(baseNodes.map((n) => decorateNode(n, highlightLogicalId, statusByAgentId, dimmedAgentIds, disabledNodeIds, frozenNodeIds, reusedNodeIds)));
    // Only on topology change — status/highlight-only updates are handled by the effect below so
    // node POSITIONS (incl. user drags) are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseNodes, setNodes]);

  // LIVE overlay: on every status/selection/cap/disable tick, recompute each existing node's style/
  // className/highlight IN PLACE (keeping its current position). ReactFlow reconciles per-node, so
  // tints (and the capped-out dim, which updates live as the user clicks Stage pills) update
  // reliably without a relayout or a hard refresh.
  useEffect(() => {
    setNodes((prev) => prev.map((n) => decorateNode(n, highlightLogicalId, statusByAgentId, dimmedAgentIds, disabledNodeIds, frozenNodeIds, reusedNodeIds)));
  }, [statusByAgentId, highlightLogicalId, dimmedAgentIds, disabledNodeIds, frozenNodeIds, reusedNodeIds, setNodes]);

  const apiRef = useRef(null);
  // Click a node → tell the room which agent to load into the shared container (room docked tree).
  const handleNodeClick = useMemo(() => (
    typeof onAgentClick === 'function'
      ? (_e, node) => { const id = nodeAgentId(node); if (id) onAgentClick(id); }
      : undefined
  ), [onAgentClick]);

  // ── Per-room disable context menu (room surface only) ─────────────────────
  // Right-click an agent node → a small PortalMenu with the disable/enable actions. The menu
  // anchors to an INVISIBLE fixed div parked at the click coordinates (``menuAnchorRef``) — the
  // standard PortalMenu anchor pattern, just with a synthetic anchor since ReactFlow nodes are
  // canvas children (no stable DOM ref per node). Only wired when ``onAgentDisableAction`` is
  // provided, so the workbench Agents-tab usage is completely unaffected.
  const disableMenuEnabled = typeof onAgentDisableAction === 'function';
  const reuseMenuEnabled = typeof onAgentReuseAction === 'function';
  const nodeMenuEnabled = disableMenuEnabled || reuseMenuEnabled;
  // {agentId, nodeId, hasChildren, x, y} | null — the open menu's target + anchor position.
  const [menuState, setMenuState] = useState(null);
  const menuAnchorRef = useRef(null);
  const closeDisableMenu = useCallback(() => setMenuState(null), []);
  const handleNodeContextMenu = useMemo(() => (
    nodeMenuEnabled
      ? (event, node) => {
        event.preventDefault();
        const aslType = node?.data?.aslType;
        // Terminals (start/stop) aren't agents — no disable menu for them.
        if (aslType !== 'builder' && aslType !== 'worker') return;
        const agentId = nodeAgentId(node);
        if (!agentId) return;
        const nodeId = String(node.id || '');
        // "Disable agent only (keep team)" only makes sense when the agent HAS a team — check for
        // any descendant node (path-prefix child) in the raw graph.
        const hasChildren = (Array.isArray(data?.nodes) ? data.nodes : [])
          .some((n) => String(n.id || '').startsWith(`${nodeId}/`));
        setMenuState({ agentId, nodeId, hasChildren, x: event.clientX, y: event.clientY });
      }
      : undefined
  ), [nodeMenuEnabled, data]);
  const fireDisableAction = useCallback((action) => {
    const target = menuState;
    setMenuState(null);
    if (target?.agentId) onAgentDisableAction(target.agentId, action);
  }, [menuState, onAgentDisableAction]);
  const fireReuseAction = useCallback((action) => {
    const target = menuState;
    setMenuState(null);
    if (target?.agentId && reuseMenuEnabled) onAgentReuseAction(target.agentId, action);
  }, [menuState, onAgentReuseAction, reuseMenuEnabled]);
  // Current disable mode of the menu's target (null = enabled) — flips the menu to "Enable agent".
  const menuTargetMode = (menuState && disabledByAgentId
    && typeof disabledByAgentId === 'object') ? (disabledByAgentId[menuState.agentId] || null) : null;
  // The reuse mark on the menu's target ({capturedMilestone, capturedRunId} | null) → flips the
  // reuse item to "Stop reusing" + surfaces "Reused from run N".
  const menuTargetReuse = (menuState && reuseByAgentId && typeof reuseByAgentId === 'object')
    ? (reuseByAgentId[menuState.agentId] || null) : null;
  // Is the target the room's IMMUTABLE frozen carve? If so, the owner can't enable it — the menu
  // shows a locked, non-actionable line instead of "Enable agent".
  const menuTargetFrozen = !!(menuState && disabledSourceByAgentId
    && typeof disabledSourceByAgentId === 'object'
    && disabledSourceByAgentId[menuState.agentId] === 'frozen');

  // HYBRID pan/zoom + auto-follow. We keep the nice "frame the selected agent" animation, but ONLY
  // when the SELECTION changes — never on a status tick. Previously this effect depended on
  // ``nodes`` (which is re-set on EVERY live status update), so fitView re-fired constantly and
  // stomped whatever the user had just panned/zoomed to — they lost manual control. Now: gate on
  // ``highlightLogicalId`` alone (via a ref that remembers the last framed selection), and read the
  // current nodes from a ref so we don't depend on them. Between selection changes the viewport is
  // fully the user's — drag to pan, wheel/pinch to zoom, Controls to zoom/fit.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const lastFramedRef = useRef(null);
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !highlightLogicalId) return;
    // Only auto-frame when the SELECTION actually changed (not a re-render / status tick).
    if (lastFramedRef.current === highlightLogicalId) return;
    const match = (nodesRef.current || []).find((n) => matchesSelected(n, highlightLogicalId));
    if (!match?.id) return;
    lastFramedRef.current = highlightLogicalId;
    const id = match.id;
    requestAnimationFrame(() => {
      try {
        // Slow, smooth glide to the selected agent (800ms) — the deliberate "focus" feel the user
        // liked, not a fast snap. It fires ONCE per selection now (see the gate above), so this is
        // the true animation the user sees — no re-fire restarting it mid-flight.
        api.fitView({ nodes: [{ id }], duration: 800, padding: 0.6, minZoom: 0.12, maxZoom: 1.4 });
      } catch {
        /* noop */
      }
    });
  }, [highlightLogicalId]);

  if (!nodes.length) {
    return (
      <div className={`flex items-center justify-center h-full text-gray-500 text-sm ${canvasClassName}`}>
        No diagram available yet
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${canvasClassName}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={(instance) => { apiRef.current = instance; }}
        onNodeClick={handleNodeClick}
        onNodeContextMenu={handleNodeContextMenu}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={!!handleNodeClick}
        proOptions={{ hideAttribution: true }}
        minZoom={0.1}
        maxZoom={1.5}
        // HYBRID control: full manual pan/zoom is always on (auto-follow only nudges on a NEW
        // selection — see the effect above). Explicit so the user can always drag to pan, scroll
        // to zoom, and pinch on trackpads — independent of the auto-frame.
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        panOnScroll={false}
        zoomOnDoubleClick
      >
        <Background gap={16} color="#e2e8f0" />
        {/* Controls include the fit-view + zoom buttons so the user can reset the framing anytime. */}
        <Controls showInteractive={false} />
      </ReactFlow>
      {/* Invisible fixed anchor at the right-click coordinates — PortalMenu positions off its
          rect. Zero-size + pointer-events-none: purely a positioning handle, never visible. */}
      {menuState ? (
        <div
          ref={menuAnchorRef}
          style={{ position: 'fixed', left: menuState.x, top: menuState.y, width: 0, height: 0, pointerEvents: 'none' }}
          aria-hidden
        />
      ) : null}
      {disableMenuEnabled ? (
        <PortalMenu
          anchorRef={menuAnchorRef}
          open={!!menuState}
          onClose={closeDisableMenu}
          align="bottom-left"
          width={220}
          className="rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {menuTargetFrozen ? (
            // IMMUTABLE frozen carve → no action. Show a locked, non-clickable explanation so the
            // owner understands they can't re-enable it here (only a re-release changes the carve).
            <div
              className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-xs text-slate-500"
              role="menuitem"
              aria-disabled="true"
              data-testid="tree-frozen-locked"
            >
              <span className="font-medium">Disabled by room carve</span>
              <span className="text-[10px] text-slate-400">Frozen with the room — re-release to change</span>
            </div>
          ) : menuTargetMode ? (
            // Already disabled (owner's 'room' tier) → the one sensible action is to turn it back on.
            <button
              type="button"
              onClick={() => fireDisableAction('enable')}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
              role="menuitem"
              data-testid="tree-enable-agent"
            >
              <span className="font-medium">Enable agent</span>
              <span className="text-[10px] text-gray-400">
                {menuTargetMode === 'self' ? 'Currently bypassed (team active)' : 'Currently disabled with its team'}
              </span>
            </button>
          ) : (
            <>
              {/* Locked naming (ROOM-SCOPES): "Disable node" = the agent AND its whole team off;
                  "Bypass agent" = only the agent skipped, its team stays routable. */}
              <button
                type="button"
                onClick={() => fireDisableAction('disable-branch')}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-xs text-red-700 hover:bg-red-50"
                role="menuitem"
                data-testid="tree-disable-branch"
              >
                <span className="font-medium">Disable node</span>
                <span className="text-[10px] text-red-400">Agent + its whole team off</span>
              </button>
              {/* Bypass is only meaningful when there IS a team — leaf agents get Disable node
                  only (the backend coerces a leaf bypass to a full disable anyway). */}
              {menuState?.hasChildren ? (
                <button
                  type="button"
                  onClick={() => fireDisableAction('disable-self')}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-xs text-amber-700 hover:bg-amber-50"
                  role="menuitem"
                  data-testid="tree-disable-self"
                >
                  <span className="font-medium">Bypass agent</span>
                  <span className="text-[10px] text-amber-500">Skip this agent — its team stays active</span>
                </button>
              ) : null}
              {/* RESULT REUSE (RESULT-REUSE.md): keep this node's last good output next run instead of
                  re-running it. Already reused → "Stop reusing" + the run it's pinned to. */}
              {reuseMenuEnabled ? (
                menuTargetReuse ? (
                  <button
                    type="button"
                    onClick={() => fireReuseAction('stop-reuse')}
                    className="flex w-full flex-col items-start gap-0.5 border-t border-gray-100 px-3 py-1.5 text-left text-xs text-sky-700 hover:bg-sky-50"
                    role="menuitem"
                    data-testid="tree-stop-reuse"
                  >
                    <span className="font-medium">Stop reusing</span>
                    <span
                      className="text-[10px] text-sky-500"
                      title={menuTargetReuse.capturedRunId ? `Reused from run ${menuTargetReuse.capturedRunId}` : undefined}
                    >
                      Reused from last run
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => fireReuseAction('reuse')}
                    className="flex w-full flex-col items-start gap-0.5 border-t border-gray-100 px-3 py-1.5 text-left text-xs text-sky-700 hover:bg-sky-50"
                    role="menuitem"
                    data-testid="tree-reuse-result"
                  >
                    <span className="font-medium">Reuse last result</span>
                    <span className="text-[10px] text-sky-500">Keep this agent&apos;s last output — don&apos;t re-run it</span>
                  </button>
                )
              ) : null}
            </>
          )}
        </PortalMenu>
      ) : null}
    </div>
  );
}
